import React, { useState, useEffect, useMemo } from 'react'
import {
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import SaveIcon from '@mui/icons-material/Save'
import { TelaResponse } from '../../types/api'
import { createLogger } from '../../utils/logger'

const logger = createLogger('TelaOrdenacaoList')

interface TelaOrdenacaoListProps {
  telas: TelaResponse[]
  tipoSistema: 'ADMIN' | 'GALATEA_WEB' | 'all'
  onReordenar: (telas: Array<{ id: number; posicao_menu: number }>) => Promise<void>
  apenasGlobais?: boolean // Se true, mostra apenas telas globais; se false, mostra apenas telas específicas
  titulo?: string // Título personalizado
  onToggleNovaAba?: (telaId: number, abrirNovaAba: boolean) => Promise<void>
}

const TelaOrdenacaoList: React.FC<TelaOrdenacaoListProps> = ({
  telas,
  tipoSistema,
  onReordenar,
  apenasGlobais = true,
  titulo,
  onToggleNovaAba,
}) => {
  const [telasOrdenadas, setTelasOrdenadas] = useState<TelaResponse[]>(telas)
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [toggleLoading, setToggleLoading] = useState<Record<number, boolean>>({})

  // Atualizar telas quando prop mudar
  useEffect(() => {
    setTelasOrdenadas(telas)
  }, [telas])

  // Filtrar telas por tipo de sistema e por tipo (globais ou específicas)
  const telasFiltradas = useMemo(() => {
    const filtradas = telasOrdenadas
      .filter((tela) => tipoSistema === 'all' || tela.tipo_sistema === tipoSistema)
      .filter((tela) => !tela.deleted_at && tela.ativo)
      .filter((tela) => {
        if (apenasGlobais) {
          // Apenas telas globais (configuração padrão)
          return !tela.empresa_id && !tela.grupo_id
        } else {
          // Apenas telas específicas de empresa/grupo
          return tela.empresa_id !== null || tela.grupo_id !== null
        }
      })
      .sort((a, b) => a.posicao_menu - b.posicao_menu)
    
    logger.debug(`Telas filtradas: ${filtradas.length} (tipo: ${tipoSistema}, apenasGlobais: ${apenasGlobais})`)
    logger.debug('Telas:', filtradas.map(t => `${t.nome} (ID: ${t.id}, Pos: ${t.posicao_menu})`))
    
    return filtradas
  }, [telasOrdenadas, tipoSistema, apenasGlobais])

  const moverParaCima = (index: number) => {
    if (index === 0) return

    const novasTelas = [...telasFiltradas]
    const telaAtual = novasTelas[index]
    const telaAnterior = novasTelas[index - 1]

    // Trocar posições (usar múltiplos de 10 para facilitar reordenação)
    const tempPosicao = telaAtual.posicao_menu
    telaAtual.posicao_menu = telaAnterior.posicao_menu
    telaAnterior.posicao_menu = tempPosicao

    logger.debug(`Movendo ${telaAtual.nome} para cima: ${tempPosicao} → ${telaAtual.posicao_menu}`)
    logger.debug(`Movendo ${telaAnterior.nome} para baixo: ${telaAnterior.posicao_menu} → ${tempPosicao}`)

    // Atualizar estado
    const novasTelasOrdenadas = telasOrdenadas.map((tela) => {
      if (tela.id === telaAtual.id) {
        return { ...tela, posicao_menu: telaAtual.posicao_menu }
      }
      if (tela.id === telaAnterior.id) {
        return { ...tela, posicao_menu: telaAnterior.posicao_menu }
      }
      return tela
    })

    setTelasOrdenadas(novasTelasOrdenadas)
    setSaveSuccess(false)
    setSaveError(null)
  }

  const moverParaBaixo = (index: number) => {
    if (index === telasFiltradas.length - 1) return

    const novasTelas = [...telasFiltradas]
    const telaAtual = novasTelas[index]
    const telaProxima = novasTelas[index + 1]

    // Trocar posições
    const tempPosicao = telaAtual.posicao_menu
    telaAtual.posicao_menu = telaProxima.posicao_menu
    telaProxima.posicao_menu = tempPosicao

    logger.debug(`Movendo ${telaAtual.nome} para baixo: ${tempPosicao} → ${telaAtual.posicao_menu}`)
    logger.debug(`Movendo ${telaProxima.nome} para cima: ${telaProxima.posicao_menu} → ${tempPosicao}`)

    // Atualizar estado
    const novasTelasOrdenadas = telasOrdenadas.map((tela) => {
      if (tela.id === telaAtual.id) {
        return { ...tela, posicao_menu: telaAtual.posicao_menu }
      }
      if (tela.id === telaProxima.id) {
        return { ...tela, posicao_menu: telaProxima.posicao_menu }
      }
      return tela
    })

    setTelasOrdenadas(novasTelasOrdenadas)
    setSaveSuccess(false)
    setSaveError(null)
  }

  const handleSalvar = async () => {
    setIsSaving(true)
    setSaveSuccess(false)
    setSaveError(null)

    try {
      // Preparar dados para envio - garantir que id e posicao_menu são números
      const telasParaEnviar = telasFiltradas.map((tela) => {
        const id = typeof tela.id === 'string' ? parseInt(tela.id, 10) : Number(tela.id)
        const posicao = typeof tela.posicao_menu === 'string' ? parseInt(tela.posicao_menu, 10) : Number(tela.posicao_menu)
        
        // Validar que são números válidos
        if (isNaN(id) || isNaN(posicao)) {
          throw new Error(`Tela ${tela.nome} tem dados inválidos: id=${tela.id}, posicao_menu=${tela.posicao_menu}`)
        }
        
        return {
          id: id,
          posicao_menu: posicao,
        }
      })

      logger.debug('Enviando reordenação:', JSON.stringify(telasParaEnviar, null, 2))
      logger.debug('Tipos dos dados:', telasParaEnviar.map(t => ({ id: typeof t.id, posicao_menu: typeof t.posicao_menu })))
      logger.debug('Formato do request:', { telas: telasParaEnviar })
      
      await onReordenar(telasParaEnviar)
      logger.debug('Reordenação salva com sucesso')
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (error: any) {
      logger.error('Erro ao salvar ordenação:', error)
      logger.error('Detalhes do erro:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        method: error.config?.method,
      })
      const errorDetail = error.response?.data?.detail
      let errorMessage = 'Erro ao salvar ordenação'
      if (errorDetail) {
        if (Array.isArray(errorDetail)) {
          errorMessage = errorDetail.map((e: any) => `${e.loc?.join('.')}: ${e.msg}`).join(', ')
        } else if (typeof errorDetail === 'string') {
          errorMessage = errorDetail
        } else {
          errorMessage = JSON.stringify(errorDetail)
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      setSaveError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggleNovaAba = async (tela: TelaResponse, value: boolean) => {
    if (!onToggleNovaAba) {
      return
    }

    setToggleLoading((prev) => ({ ...prev, [tela.id]: true }))
    setSaveError(null)

    try {
      await onToggleNovaAba(tela.id, value)
      setTelasOrdenadas((prev) =>
        prev.map((item) =>
          item.id === tela.id ? { ...item, abrir_nova_aba: value } : item
        )
      )
    } catch (error: any) {
      logger.error('Erro ao atualizar comportamento da tela:', error)
      const errorDetail = error.response?.data?.detail
      let errorMessage = 'Erro ao atualizar comportamento da tela'
      if (errorDetail) {
        if (Array.isArray(errorDetail)) {
          errorMessage = errorDetail.map((e: any) => `${e.loc?.join('.')}: ${e.msg}`).join(', ')
        } else if (typeof errorDetail === 'string') {
          errorMessage = errorDetail
        } else {
          errorMessage = JSON.stringify(errorDetail)
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      setSaveError(errorMessage)
    } finally {
      setToggleLoading((prev) => {
        const novo = { ...prev }
        delete novo[tela.id]
        return novo
      })
    }
  }

  return (
    <Box>
      {apenasGlobais && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" component="div">
            <strong>Configuração Padrão Global</strong>
            <br />
            Você está alterando a ordem padrão das telas, que se aplica a <strong>TODAS as empresas</strong>.
            <br />
            Esta ordem será respeitada por todas as áreas funcionais e níveis de acesso.
            <br />
            <strong>Nota:</strong> As permissões específicas (por empresa, área funcional, nível ou usuário) continuam valendo.
            <br />
            Apenas telas globais (sem empresa/grupo específico) são exibidas aqui.
          </Typography>
        </Alert>
      )}

      {!apenasGlobais && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" component="div">
            <strong>Telas Específicas da Empresa/Grupo</strong>
            <br />
            Você está alterando a ordem das telas específicas da sua empresa ou grupo.
            <br />
            Esta ordem se aplica apenas à sua empresa/grupo e não afeta outras empresas.
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          {titulo || (apenasGlobais ? 'Ordenação de Telas - Configuração Padrão' : 'Ordenação de Telas - Empresa/Grupo')} ({telasFiltradas.length} telas)
        </Typography>
        <Button
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSalvar}
          disabled={isSaving || telasFiltradas.length === 0}
        >
          Salvar Ordenação Padrão
        </Button>
      </Box>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Ordenação salva com sucesso!
        </Alert>
      )}

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      <Paper>
        <List>
          {telasFiltradas.map((tela, index) => (
            <ListItem
              key={tela.id}
              secondaryAction={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {apenasGlobais && onToggleNovaAba && (
                    <Tooltip title="Define se a tela abre em uma nova aba">
                      <FormControlLabel
                        control={
                          <Switch
                            edge="start"
                            size="small"
                            checked={Boolean(tela.abrir_nova_aba)}
                            onChange={(_, value) => handleToggleNovaAba(tela, value)}
                            disabled={Boolean(toggleLoading[tela.id])}
                          />
                        }
                        label="Nova aba"
                      />
                    </Tooltip>
                  )}
                  <IconButton
                    edge="end"
                    onClick={() => moverParaCima(index)}
                    disabled={index === 0}
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    <ArrowUpwardIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => moverParaBaixo(index)}
                    disabled={index === telasFiltradas.length - 1}
                    size="small"
                  >
                    <ArrowDownwardIcon />
                  </IconButton>
                </Box>
              }
            >
              <ListItemText
                primary={tela.nome}
                secondary={
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Rota: {tela.rota} | Posição: {tela.posicao_menu} | Tipo: {tela.tipo_sistema}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>
  )
}

export default TelaOrdenacaoList

